"use client";

import { Canvas } from "@react-three/fiber";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";
import clsx from "clsx";
import { useAnimationFrame, useMotionValue, useSpring as useMotionSpring } from "motion/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useMove } from "react-aria";

import {
  createCarouselStore,
  createInitialCarouselState,
  decayDragVelocity,
  DRAG_START_DISTANCE_THRESHOLD,
  DRAG_VELOCITY_FILTER_ALPHA,
  DRAG_VELOCITY_MAX_SAMPLE_INTERVAL_SECONDS,
  getDragVelocityFromSamples,
  getRecentDragVelocitySamples,
  getVisibleCoverSlots,
  type CarouselAction,
  type DragVelocitySample,
} from "./AlbumCoverCarousel";
import { getInversePerspectiveScale, getRotationForIndex, getRotationIndexFromRotation } from "./AlbumCoverGeometry";
import {
  AlbumCoverScene,
  OVERLAY_CARD_BACKGROUND,
  OVERLAY_CARD_BORDER_RADIUS,
  OVERLAY_CARD_CONTROL_PADDING,
  OVERLAY_CARD_TIMELINE_LEFT_PADDING,
  PLAY_BUTTON_LINE_GAP,
  PLAY_BUTTON_TIMELINE_MARGIN_BOTTOM,
} from "./AlbumCoverScene";
import type { AlbumCover, AlbumCoverVisualProps } from "./AlbumCoverTypes";
import {
  DEFAULT_HERO_MUSIC_VISUAL_CONTROLS,
  HERO_MUSIC_VISUAL_FRAME_CLASS_NAME,
  HERO_MUSIC_VISUAL_LAYOUT_STYLE,
  HERO_MUSIC_VISUAL_OVERLAY_CLASS_NAME,
  getHeroMusicOverlayPerspective,
  getHeroMusicOverlayScale,
} from "./HeroMusicVisualControls";
import { HERO_MUSIC_TIMELINE_GROUP_IDS, HeroMusicTimelineBar } from "./HeroMusicTimelineBar";
import { clampValue, getWrappedIndex } from "./HeroMusicUtils";
import { NavArrowButton } from "./NavArrowButton";

export type {
  AlbumCover,
  AlbumCoverVariant,
  AlbumCoverVisualProps,
  SeekPlaybackOptions,
  TogglePlaybackOptions,
} from "./AlbumCoverTypes";

type OverlayCardPointerStart = {
  coverIndex: number;
  pointerId: number;
  x: number;
  y: number;
};

const PLAYBACK_PROGRESS_SPRING = {
  stiffness: 120,
  damping: 28,
  mass: 0.2,
};
const HERO_MUSIC_CONTROL_SELECTOR = "[data-hero-music-control]";

export function AlbumCoverVisual({
  activeCoverIndex,
  activeTimelineGroup,
  covers,
  getPlaybackProgress,
  nextLabel,
  onActiveCoverIndexChange,
  heightMode = "fixed",
  onSeekPlaybackProgress,
  onTogglePlay,
  pauseLabel,
  playLabel,
  playingCoverIndex,
  previousLabel,
}: AlbumCoverVisualProps) {
  const visualControls = DEFAULT_HERO_MUSIC_VISUAL_CONTROLS;
  const carouselItemCount = covers.length;
  const coverSlotCount = Math.max(1, carouselItemCount);
  const normalizedActiveCoverIndex = carouselItemCount > 0 ? getWrappedIndex(activeCoverIndex, carouselItemCount) : 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayCardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const carouselStoreRef = useRef<ReturnType<typeof createCarouselStore> | null>(null);

  if (!carouselStoreRef.current) {
    carouselStoreRef.current = createCarouselStore(createInitialCarouselState(normalizedActiveCoverIndex));
  }

  const carouselStore = carouselStoreRef.current;
  const carousel = useSyncExternalStore(carouselStore.subscribe, carouselStore.getSnapshot, carouselStore.getSnapshot);
  const fallbackRenderedRotationIndexRef = useRef(carousel.renderedRotationIndex);
  const captureRenderedRotationIndexRef = useRef(() => fallbackRenderedRotationIndexRef.current);
  const lastControlledActiveIndexRef = useRef(normalizedActiveCoverIndex);
  const pendingTargetActiveIndexRef = useRef<number | null>(null);
  const overlayCardPointerStartRef = useRef<OverlayCardPointerStart | null>(null);
  const targetPlaybackProgress = useMotionValue(0);
  const playbackProgress = useMotionSpring(targetPlaybackProgress, PLAYBACK_PROGRESS_SPRING);
  const inactivePlaybackProgress = useMotionValue(0);
  const moveDistanceRef = useRef(0);
  const dragVelocityRef = useRef(0);
  const dragVelocitySamplesRef = useRef<DragVelocitySample[]>([]);
  const lastDragTimestampRef = useRef(0);
  const hasActiveDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const suppressClickTimeoutRef = useRef<number | undefined>(undefined);
  const carouselActiveCoverIndex = carouselItemCount > 0 ? getWrappedIndex(carousel.activeIndex, carouselItemCount) : 0;
  const rotationY = getRotationForIndex(carousel.renderedRotationIndex, coverSlotCount);
  const isDragging = carousel.status === "dragging";
  fallbackRenderedRotationIndexRef.current = carousel.renderedRotationIndex;
  const visibleCoverSlots = useMemo(
    () => getVisibleCoverSlots(covers, carouselActiveCoverIndex, carousel.targetRotationIndex, coverSlotCount),
    [carouselActiveCoverIndex, carousel.targetRotationIndex, coverSlotCount, covers],
  );

  const applyCarouselAction = useCallback(
    (action: CarouselAction, options: { notifyActiveChange?: boolean } = {}) => {
      const { previousState, nextState } = carouselStore.dispatch(action);

      if (options.notifyActiveChange === false) return;
      if (nextState.activeIndex === previousState.activeIndex) return;

      pendingTargetActiveIndexRef.current = nextState.activeIndex;
      onActiveCoverIndexChange(nextState.activeIndex, previousState.activeIndex);
    },
    [carouselStore, onActiveCoverIndexChange],
  );

  useAnimationFrame(() => {
    if (playingCoverIndex !== carouselActiveCoverIndex) return;

    targetPlaybackProgress.set(clampPlaybackProgress(getPlaybackProgress(carouselActiveCoverIndex)));
  });

  useEffect(() => {
    targetPlaybackProgress.set(0);
  }, [carouselActiveCoverIndex, activeTimelineGroup, targetPlaybackProgress]);

  useEffect(() => {
    const pendingTargetActiveIndex = pendingTargetActiveIndexRef.current;

    if (pendingTargetActiveIndex != null) {
      const normalizedPendingTargetIndex =
        carouselItemCount > 0 ? getWrappedIndex(pendingTargetActiveIndex, carouselItemCount) : 0;

      if (normalizedActiveCoverIndex === normalizedPendingTargetIndex) {
        pendingTargetActiveIndexRef.current = null;
        lastControlledActiveIndexRef.current = normalizedActiveCoverIndex;
        return;
      }

      if (normalizedActiveCoverIndex === lastControlledActiveIndexRef.current) return;

      pendingTargetActiveIndexRef.current = null;
    }

    if (normalizedActiveCoverIndex === lastControlledActiveIndexRef.current) return;

    lastControlledActiveIndexRef.current = normalizedActiveCoverIndex;

    applyCarouselAction(
      { type: "syncActiveIndex", activeIndex: normalizedActiveCoverIndex, itemCount: carouselItemCount },
      { notifyActiveChange: false },
    );
  }, [applyCarouselAction, carouselItemCount, normalizedActiveCoverIndex]);

  useEffect(() => {
    return () => {
      if (suppressClickTimeoutRef.current) {
        window.clearTimeout(suppressClickTimeoutRef.current);
      }
    };
  }, []);

  const heightClass = heightMode === "flexible" ? "tw-h-full" : HERO_MUSIC_VISUAL_FRAME_CLASS_NAME;
  const containerStyle = {
    ...HERO_MUSIC_VISUAL_LAYOUT_STYLE,
    maxWidth: `${visualControls.maxContainerWidth}px`,
    touchAction: "none" as const,
  };

  function resetMoveTracking() {
    moveDistanceRef.current = 0;
    dragVelocityRef.current = 0;
    dragVelocitySamplesRef.current = [];
    lastDragTimestampRef.current = performance.now();
    hasActiveDragRef.current = false;
  }

  function trackDragVelocity(deltaIndex: number, timestamp: number) {
    const elapsedSeconds = (timestamp - lastDragTimestampRef.current) / 1000;

    if (elapsedSeconds > 0.001) {
      const sampledElapsedSeconds = Math.min(elapsedSeconds, DRAG_VELOCITY_MAX_SAMPLE_INTERVAL_SECONDS);
      const instantaneousVelocity = deltaIndex / sampledElapsedSeconds;

      dragVelocityRef.current += (instantaneousVelocity - dragVelocityRef.current) * DRAG_VELOCITY_FILTER_ALPHA;
      dragVelocitySamplesRef.current.push({
        deltaIndex,
        elapsedSeconds: sampledElapsedSeconds,
        timestamp,
      });
      dragVelocitySamplesRef.current = getRecentDragVelocitySamples(dragVelocitySamplesRef.current, timestamp);
    }

    lastDragTimestampRef.current = timestamp;
  }

  function getReleaseVelocity(timestamp: number) {
    const elapsedSeconds = (timestamp - lastDragTimestampRef.current) / 1000;
    const sampledVelocity = getDragVelocityFromSamples(dragVelocitySamplesRef.current, timestamp);
    const releaseVelocity =
      Math.abs(sampledVelocity) > Math.abs(dragVelocityRef.current) ? sampledVelocity : dragVelocityRef.current;

    return decayDragVelocity(releaseVelocity, Math.max(elapsedSeconds, 0));
  }

  function clearDeferredClickSuppression() {
    if (suppressClickTimeoutRef.current) {
      window.clearTimeout(suppressClickTimeoutRef.current);
    }

    suppressClickTimeoutRef.current = window.setTimeout(() => {
      suppressClickRef.current = false;
      suppressClickTimeoutRef.current = undefined;
    }, 0);
  }

  function applyRotationDelta(deltaRotation: number, timestamp: number) {
    const deltaIndex = getRotationIndexFromRotation(deltaRotation, coverSlotCount);

    applyCarouselAction({ type: "dragBy", deltaIndex });
    trackDragVelocity(deltaIndex, timestamp);
  }

  function getDragRotationDelta(deltaX: number) {
    return (deltaX / getDragRotationSensitivityPx()) * visualControls.dragResponsiveness;
  }

  function getDragRotationSensitivityPx() {
    const containerHeight = containerRef.current?.offsetHeight ?? visualControls.height.mobile;
    const overlayPerspective = getHeroMusicOverlayPerspective(visualControls, containerHeight);
    const overlayRadiusPx =
      (visualControls.radius + visualControls.coverRadiusOffset) *
      getHeroMusicOverlayScale(overlayPerspective, visualControls.cameraZ);

    return Math.max(1, overlayRadiusPx / getInversePerspectiveScale(overlayPerspective, overlayRadiusPx));
  }

  function getCurrentRenderedRotationIndex() {
    const renderedRotationIndex = captureRenderedRotationIndexRef.current();

    if (Number.isFinite(renderedRotationIndex)) return renderedRotationIndex;

    return carouselStore.getSnapshot().renderedRotationIndex;
  }

  function navigate(direction: 1 | -1) {
    applyCarouselAction({
      type: "moveBy",
      deltaIndex: direction,
      itemCount: carouselItemCount,
    });
  }

  function selectCoverIndex(coverIndex: number) {
    if (suppressClickRef.current) return;

    applyCarouselAction({
      type: "selectCoverIndex",
      coverIndex,
      itemCount: carouselItemCount,
    });
  }

  function handleSeekPlaybackProgress(coverIndex: number, progress: number, seconds: number) {
    const clampedProgress = clampPlaybackProgress(progress);

    targetPlaybackProgress.set(clampedProgress);
    onSeekPlaybackProgress(coverIndex, seconds, { play: true });
  }

  function toggleCoverPlayback(coverIndex: number) {
    const shouldRestartPlayback = coverIndex !== carouselActiveCoverIndex;

    if (shouldRestartPlayback) {
      applyCarouselAction({
        type: "selectCoverIndex",
        coverIndex,
        itemCount: carouselItemCount,
      });
    }

    onTogglePlay(coverIndex, { restart: shouldRestartPlayback });
  }

  function handleOverlayCardPointerDown(event: ReactPointerEvent<HTMLDivElement>, coverIndex: number) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (isHeroMusicControlEvent(event)) {
      overlayCardPointerStartRef.current = null;
      return;
    }

    overlayCardPointerStartRef.current = {
      coverIndex,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }

  function handleOverlayCardPointerUp(event: ReactPointerEvent<HTMLDivElement>, coverIndex: number) {
    if (isHeroMusicControlEvent(event)) {
      overlayCardPointerStartRef.current = null;
      return;
    }

    const pointerStart = overlayCardPointerStartRef.current;

    overlayCardPointerStartRef.current = null;

    if (!pointerStart) return;
    if (pointerStart.pointerId !== event.pointerId) return;
    if (pointerStart.coverIndex !== coverIndex) return;
    if (hasActiveDragRef.current) return;

    const movement = Math.abs(event.clientX - pointerStart.x) + Math.abs(event.clientY - pointerStart.y);

    if (movement > DRAG_START_DISTANCE_THRESHOLD) return;

    selectCoverIndex(coverIndex);
  }

  const { moveProps } = useMove({
    onMoveStart: () => {
      resetMoveTracking();
    },
    onMoveEnd: () => {
      if (!hasActiveDragRef.current) {
        resetMoveTracking();
        return;
      }

      suppressClickRef.current = true;
      clearDeferredClickSuppression();
      applyCarouselAction({
        type: "endDrag",
        itemCount: carouselItemCount,
        releaseVelocity: getReleaseVelocity(performance.now()),
      });
      resetMoveTracking();
    },
    onMove: e => {
      if (e.pointerType === "keyboard") return;
      const timestamp = performance.now();
      const delta = getDragRotationDelta(e.deltaX);
      const movement = Math.abs(e.deltaX) + Math.abs(e.deltaY);
      const previousDistance = moveDistanceRef.current;
      const nextDistance = previousDistance + movement;

      moveDistanceRef.current = nextDistance;

      if (!hasActiveDragRef.current) {
        if (nextDistance <= DRAG_START_DISTANCE_THRESHOLD) return;

        hasActiveDragRef.current = true;
        applyCarouselAction({ type: "startDragAt", renderedRotationIndex: getCurrentRenderedRotationIndex() });

        const movementPastThreshold = nextDistance - DRAG_START_DISTANCE_THRESHOLD;
        const thresholdAdjustedDelta = movement > 0 ? delta * Math.min(1, movementPastThreshold / movement) : 0;
        applyRotationDelta(thresholdAdjustedDelta, timestamp);
        return;
      }

      applyRotationDelta(delta, timestamp);
    },
  });

  return (
    <div
      ref={containerRef}
      {...moveProps}
      className={clsx(
        "tw-relative tw-mx-auto tw-w-full",
        heightClass,
        HERO_MUSIC_VISUAL_OVERLAY_CLASS_NAME,
        visualControls.clipOverflow ? "tw-overflow-hidden" : "tw-overflow-visible",
        isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab",
      )}
      style={containerStyle}
    >
      <Canvas flat linear>
        <AlbumCoverScene
          activeTimelineGroup={activeTimelineGroup}
          captureRenderedRotationIndexRef={captureRenderedRotationIndexRef}
          controls={visualControls}
          coverSlotCount={coverSlotCount}
          covers={covers}
          isDragging={isDragging}
          overlayCardRefs={overlayCardRefs}
          radius={visualControls.radius}
          rotationY={rotationY}
          visibleCoverSlots={visibleCoverSlots}
        />
      </Canvas>
      <div
        className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center'
        data-hero-music-control
      >
        <div className='tw-flex tw-w-[min(32rem,100%)] tw-items-center tw-justify-between lg:tw-w-[min(40rem,100%)]'>
          <NavArrowButton
            ariaLabel={previousLabel}
            className='tw-pointer-events-auto'
            direction={-1}
            onClick={() => navigate(-1)}
          />
          <NavArrowButton
            ariaLabel={nextLabel}
            className='tw-pointer-events-auto'
            direction={1}
            onClick={() => navigate(1)}
          />
        </div>
      </div>
      <div
        className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10'
        style={{
          perspective: "var(--hero-music-overlay-perspective)",
          perspectiveOrigin: "50% 50%",
          transformStyle: "preserve-3d",
        }}
      >
        {visibleCoverSlots.map(({ cover, coverIndex, slotId }) => {
          const isActiveCover = coverIndex === carouselActiveCoverIndex;
          const isCoverPlaying = playingCoverIndex === coverIndex;
          const coverVariant = cover.variants[activeTimelineGroup];
          const hasAudio = Boolean(coverVariant.audioUrl);
          const timelineSectionSlotCount = getTimelineSectionSlotCount(cover);

          return (
            <div
              key={slotId}
              ref={node => {
                overlayCardRefs.current[slotId] = node;
              }}
              className='tw-absolute'
              data-cover-id={cover.id}
              data-cover-index={coverIndex}
              style={{
                backgroundColor: visualControls.redOverlayEnabled ? OVERLAY_CARD_BACKGROUND : "transparent",
                borderRadius: `${OVERLAY_CARD_BORDER_RADIUS}px`,
                height: "var(--hero-music-cover-height)",
                left: "50%",
                opacity: 0,
                overflow: "hidden",
                pointerEvents: "var(--overlay-card-pointer-events, none)" as CSSProperties["pointerEvents"],
                transform: "translateZ(0)",
                transformOrigin: "50% 50%",
                transformStyle: "preserve-3d",
                top: "50%",
                visibility: "hidden",
                width: "var(--hero-music-cover-width)",
                willChange: "opacity, transform",
              }}
              onPointerDown={event => handleOverlayCardPointerDown(event, coverIndex)}
              onPointerUp={event => handleOverlayCardPointerUp(event, coverIndex)}
            >
              <div
                className='tw-pointer-events-none tw-absolute tw-flex tw-items-end'
                data-hero-music-control
                style={{
                  bottom: `calc(${OVERLAY_CARD_CONTROL_PADDING}px * var(--overlay-card-inverse-scale, 1))`,
                  left: `calc(${OVERLAY_CARD_CONTROL_PADDING}px * var(--overlay-card-inverse-scale, 1))`,
                  backfaceVisibility: "hidden",
                  transform:
                    "rotate(var(--overlay-card-counter-rotation-z, 0rad)) rotateY(var(--overlay-card-counter-rotation-y, 0rad)) scale(var(--overlay-card-inverse-scale, 1))",
                  transformOrigin: "0 100%",
                  transformStyle: "preserve-3d",
                  willChange: "transform",
                }}
              >
                <PlayPauseButton
                  aria-label={isCoverPlaying ? pauseLabel : playLabel}
                  className='tw-pointer-events-auto tw-text-black enabled:hover:tw-bg-white'
                  data-hero-music-control
                  disabled={!hasAudio}
                  isPlaying={isCoverPlaying}
                  onClick={event => {
                    event.stopPropagation();
                    toggleCoverPlayback(coverIndex);
                  }}
                  onPointerDown={event => {
                    event.stopPropagation();
                  }}
                  onPointerUp={event => {
                    event.stopPropagation();
                  }}
                  size='pill-lg'
                  style={{
                    pointerEvents: "var(--overlay-play-pointer-events, none)" as CSSProperties["pointerEvents"],
                  }}
                  type='button'
                  variant='elevated'
                />
                <div
                  className='tw-pointer-events-auto'
                  data-hero-music-control
                  style={{
                    marginBottom: `${PLAY_BUTTON_TIMELINE_MARGIN_BOTTOM}px`,
                    marginLeft: `${PLAY_BUTTON_LINE_GAP + OVERLAY_CARD_TIMELINE_LEFT_PADDING}px`,
                    pointerEvents: "var(--overlay-play-pointer-events, none)" as CSSProperties["pointerEvents"],
                    width: "var(--overlay-timeline-width, 0px)",
                  }}
                >
                  <HeroMusicTimelineBar
                    activeProgress={isActiveCover ? targetPlaybackProgress : inactivePlaybackProgress}
                    onSeekProgress={
                      isActiveCover && hasAudio
                        ? (progress, seconds) => handleSeekPlaybackProgress(coverIndex, progress, seconds)
                        : undefined
                    }
                    progress={isActiveCover ? playbackProgress : inactivePlaybackProgress}
                    sectionSlotCount={timelineSectionSlotCount}
                    timeline={coverVariant.timeline}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getTimelineSectionSlotCount(cover: AlbumCover) {
  return Math.max(...HERO_MUSIC_TIMELINE_GROUP_IDS.map(groupId => cover.variants[groupId].timeline.sections.length));
}

function isHeroMusicControlEvent(event: ReactPointerEvent<HTMLElement>) {
  return event.target instanceof Element && Boolean(event.target.closest(HERO_MUSIC_CONTROL_SELECTOR));
}

function clampPlaybackProgress(progress: number) {
  if (!Number.isFinite(progress)) return 0;

  return clampValue(progress, 0, 1);
}
